import api from './api';

export interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roleId: number;
  role?: {
    id: number;
    name: string;
    description?: string | null;
  };
  organizationName?: string | null;
  departmentName?: string | null;
  billingPostalCode?: string | null;
  billingPrefecture?: string | null;
  billingCity?: string | null;
  billingAddressLine1?: string | null;
  billingAddressLine2?: string | null;
  billingPhone?: string | null;
  billingInvoiceRegNumber?: string | null;
  isActive: boolean;
  lastLogin?: string | null;
  timezone?: string | null;
  language?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  language: string;
  timezone: string;
  /** お知らせメールを受け取るか。省略時は受信ON */
  notifyEmail?: boolean;
  // 請求先情報（任意項目）
  billingPostalCode?: string;
  billingPrefecture?: string;
  billingCity?: string;
  billingAddressLine1?: string;
  billingAddressLine2?: string;
  billingPhone?: string;
  billingInvoiceRegNumber?: string;
  roleId?: number;
}

export interface AuthResponse {
  success: boolean;
  data: {
    user?: User;
    token?: string;
    totpRequired?: boolean;
    totpSetupRequired?: boolean;
    challenge?: string;
    secret?: string;
    otpauthUrl?: string;
    qrDataUrl?: string | null;
    recoveryCodes?: string[];
  };
}

export interface UserResponse {
  success: boolean;
  data: {
    user: User;
  };
}

export const authService = {
  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  async confirmTotp(challenge: string, code: string): Promise<AuthResponse> {
    const response = await api.post('/auth/totp/confirm', { challenge, code });
    return response.data;
  },

  async verifyTotp(challenge: string, code: string): Promise<AuthResponse> {
    const response = await api.post('/auth/totp/verify', { challenge, code });
    return response.data;
  },

  async reconfirmPassword(password: string): Promise<{ success: boolean }> {
    const response = await api.post('/auth/reconfirm', { password });
    return response.data;
  },

  async logout(): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  async register(data: RegisterRequest): Promise<UserResponse> {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  async getMe(): Promise<UserResponse> {
    const response = await api.get('/auth/me');
    return response.data;
  },

  async updateProfile(data: Partial<User>): Promise<UserResponse> {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },

  async updatePassword(data: { currentPassword: string; newPassword: string }): Promise<{ success: boolean; message: string }> {
    const response = await api.put('/auth/password', data);
    return response.data;
  },

  async requestPasswordReset(email: string): Promise<{ success: boolean; message: string; token?: string }> {
    const response = await api.post('/auth/password/reset-request', { email });
    return response.data;
  },

  async verifyPasswordResetToken(token: string): Promise<{ success: boolean; userId?: number; email?: string }> {
    const response = await api.get('/auth/password/verify-token', { params: { token } });
    return response.data;
  },

  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/auth/password/reset', { token, newPassword });
    return response.data;
  },

  async checkEmailExists(email: string): Promise<{ success: boolean; data: { exists: boolean } }> {
    const response = await api.get('/auth/check-email', { params: { email } });
    return response.data;
  },

  async checkUsernameExists(username: string): Promise<{ success: boolean; data: { exists: boolean } }> {
    const response = await api.get('/auth/check-username', { params: { username } });
    return response.data;
  },
};
